<script lang="ts" setup>
import { ChannelState } from '@/composition/channels'
import { useGlobalStore } from '@/store/app'
import { useNotification } from '@gohighlevel/ghl-ui'
import { useRoute } from 'vue-router'

import { app } from '@/composition/app'
import { GroupState } from '@/composition/groups'
import { GroupMemberStatus } from '@/models/GroupMember'
import { ChannelRoles } from '@/models/Users'
import { router } from '@/router'
import {
  PropType,
  computed,
  defineAsyncComponent,
  nextTick,
  onMounted,
  ref,
  watch,
} from 'vue'
import { useI18n } from 'vue-i18n'
import * as yup from 'yup'

const ChannelSettingsMobile = defineAsyncComponent(
  () => import('./mobile/ChannelSettingsMobile.vue')
)
const CreateChannelMobile = defineAsyncComponent(
  () => import('./mobile/CreateChannelMobile.vue')
)
const ChannelSettingsWeb = defineAsyncComponent(
  () => import('./web/ChannelSettingsWeb.vue')
)
const CreateChannelWeb = defineAsyncComponent(
  () => import('./web/CreateChannelWeb.vue')
)
const AddChannelMembers = defineAsyncComponent(
  () => import('./AddChannelMember.vue')
)
const UIConfirmation = defineAsyncComponent(
  () => import('@/components/common/modal/UIConfirmation.vue')
)

const openModalFlag = ref(false)
const route = useRoute()

const props = defineProps({
  type: {
    type: String as PropType<'create' | 'update'>,
    required: true,
  },
  show: {
    type: Boolean,
  },
})
const emit = defineEmits(['update:confirmed', 'update:show'])
const isLoadingModal = ref(false)
const notification = useNotification()
const fetchingChannelMembers = ref(false)
const pageNo = ref(1)
const nextPageAvailable = ref(true)
const currentActiveTab = ref('about')
const managersListView = ref(false)
const selectedRole = ref('ALL')
const showModal = ref(false)
const isFetchingNextPage = ref(false)
const showAddChannelMembers = ref(false)
const iconPlaceHolderText = ref('Select icons')
const showIcons = ref(false)
const { t } = useI18n()

let modalTitle =
  props?.type == t('common.create').toLowerCase()
    ? t('channel.addChannel')
    : t('channel.editChannel')
let modalHelperText =
  props?.type == t('common.create').toLowerCase()
    ? t('channel.addChannelHelperText')
    : ''
const buttonText =
  props?.type == t('common.create').toLowerCase()
    ? t('channel.createChannel')
    : t('channel.updateChannel')

const settingsItems = [
  {
    key: 'about',
    displayName: t('common.about'),
    show: () => true,
  },
  {
    key: 'members',
    displayName: t('common.members'),
    show: () => true,
  },
  {
    key: 'settings',
    displayName: t('common.settings'),
    show: () =>
      !app.state.isMobile &&
      ((channelData.value.visibility == 'PRIVATE' &&
        channelData.value.userRole === ChannelRoles.MANAGER) ||
        (channelData.value.visibility == 'PUBLIC' &&
          GroupState.state.isGroupAdmin)),
  },
]

onMounted(async () => {
  fetchChannelDetails()
  showModal.value = props.show
})

watch(
  () => props.show,
  (showModalVal: boolean) => {
    showModal.value = showModalVal
  }
)
watch(
  () => showModal.value,
  (showModalVal: boolean) => {
    if (app.state.isMobile) {
      emit('update:show', showModalVal)
    }
  }
)

watch(
  () => route.params.channelSlug,
  _ => {
    fetchChannelDetails()
  }
)

const channelData = computed(() => {
  return ChannelState.state.currentChannelData
})

const channelMembers = computed(() => {
  return ChannelState.state.currentChannelData.channelMembers
})

const rules = {
  name: {
    required: true,
    validator: (_, value: string) => {
      return yup.string().required().validate(value.trim()) as any
    },
    message: t('validations.nameRequired'),
    trigger: ['input', 'blur'],
  },
  description: {
    validator: (_, value: string) => {
      // empty string is allowed, but only spaces are not
      if (value == '') return true
      return yup.string().required().validate(value.trim()) as any
    },
    message: t('validations.invalidDescription'),
    trigger: ['input', 'blur'],
  },
}

function notificationAlert(title: string, content: string) {
  notification.create({
    title: title,
    content: content,
    duration: 3000,
  })
}
async function updateOrCreateChannel(channelData: any = {}) {
  if (
    channelData.icon == 'Select Icon' ||
    channelData.icon == 'Channel Icon' ||
    channelData.icon == '' ||
    !channelData.icon
  ) {
    channelData.icon = 'Hash02Icon'
  }

  isLoadingModal.value = true
  if (props.type == 'update') {
    try {
      await ChannelState.updateChannel({
        name: channelData.name,
        description: channelData.description,
        icon: channelData.icon,
        visibility: channelData.type,
        groupId: useGlobalStore().$state.groupId,
        locationId: useGlobalStore().$state.locationId,
      })
      notificationAlert(
        t('channel.notifications.updated.title'),
        t('channel.notifications.updated.content')
      )
    } catch (_error) {
      notificationAlert(
        t('channel.notifications.error.updateFailed.title'),
        t('channel.notifications.error.updateFailed.content')
      )
    }
  } else {
    try {
      const channel: any = await ChannelState.createChannel({
        name: channelData.name,
        description: channelData.description,
        icon: channelData.icon,
        visibility: channelData.type,
        groupId: useGlobalStore().$state.groupId,
        locationId: useGlobalStore().$state.locationId,
        isAnnouncement: channelData.isAnnouncement,
      })
      await ChannelState.fetchChannels(true)
      router.push({
        name: 'channel-overview',
        params: {
          groupSlug: route.params.groupSlug,
          channelSlug: channel.slug,
        },
      })
      notificationAlert(
        t('channel.notifications.created.title'),
        t('channel.notifications.created.content')
      )
    } catch (error) {
      console.error(error)
      notificationAlert(
        t('channel.notifications.error.createFailed.title'),
        t('channel.notifications.error.createFailed.content')
      )
    } finally {
      closeChannelSetting()
    }
  }

  isLoadingModal.value = false
  openModalFlag.value = false
}

function closeChannelSetting() {
  emit('update:confirmed')

  nextTick(() => {
    showModal.value = false
  })
}

function handleShowAddChannelMembers(value: boolean) {
  showAddChannelMembers.value = value
}

const showDeleteConfirmation = ref(false)
function handleDeleteChannel() {
  showDeleteConfirmation.value = true
  openModalFlag.value = false
}

function closeDeleteConfirmation() {
  openModalFlag.value = true
  showDeleteConfirmation.value = false
  closeChannelSetting()
}

const deletingChannel = ref(false)
async function deleteChannel() {
  deletingChannel.value = true
  await ChannelState.deleteChannel()
  router.push({ name: 'home', params: { groupSlug: route.params.groupSlug } })
  deletingChannel.value = false
  closeDeleteConfirmation()
  openModalFlag.value = false
  notificationAlert(
    t('channel.notifications.deleted.title'),
    t('channel.notifications.deleted.content')
  )
}

const showVisibilityChangeConfirmation = ref(false)
const showPrivateToPublicConfirmation = ref(false)
const showEnableAnnouncementConfirmation = ref(false)
const showDisableAnnouncementConfirmation = ref(false)
async function handleChangePublicToPrivate() {
  try {
    // update visibility to private
    await ChannelState.updateVisibility(
      ChannelState.state.currentChannelData._id,
      'PRIVATE'
    )
    notification.create({
      type: 'success',
      title: t('channel.notifications.updated.title'),
      content: t('channel.notifications.visibilityChangedToPrivate', {
        channelName: ChannelState.state.currentChannelData.name,
      }),
      duration: 3000,
    })
    await ChannelState.fetchChannels(true)
    closeChannelSetting()
  } catch (_error) {
    notification.create({
      type: 'error',
      title: 'An error occurred...',
      content: `An error occurred while attempting to change visibility of ${ChannelState.state.currentChannelData.name}, please try after sometime.`,
      duration: 3000,
    })
  }
}

async function handleChangePrivateToPublic() {
  try {
    // update visibility to public
    await ChannelState.updateVisibility(
      ChannelState.state.currentChannelData._id,
      'PUBLIC'
    )
    notification.create({
      type: 'success',
      title: t('channel.notifications.updated.title'),
      content: t('channel.notifications.visibilityChangedToPublic', {
        channelName: ChannelState.state.currentChannelData.name,
      }),
      duration: 3000,
    })
    await ChannelState.fetchChannels(true)
    closeChannelSetting()
  } catch (_error) {
    notification.create({
      type: 'error',
      title: 'An error occurred...',
      content: `An error occurred while attempting to change visibility of ${ChannelState.state.currentChannelData.name}, please try after sometime.`,
      duration: 3000,
    })
  }
}

async function setAnnouncementPostingState(isEnabled: boolean) {
  ChannelState.state.currentChannelData.isAnnouncement = isEnabled
  try {
    await ChannelState.updateChannelAnnouncement(isEnabled)
    notification.create({
      type: 'success',
      title: isEnabled
        ? t('channel.notifications.memberPostingDisabled.title')
        : t('channel.notifications.memberPostingEnabled.title'),
      content: isEnabled
        ? t('channel.notifications.memberPostingDisabled.content')
        : t('channel.notifications.memberPostingEnabled.content'),
      duration: 3000,
    })
    closeChannelSetting()
  } catch (_error) {
    console.error(_error)
  }
}

const showLeaveConfirmation = ref(false)
async function handleLeaveChannel() {
  try {
    await ChannelState.leaveChannel(ChannelState.state.currentChannelData._id)
    notification.create({
      type: 'success',
      title: 'Successfully left Channel',
      content: `Successfully left ${ChannelState.state.currentChannelData.name}`,
      duration: 3000,
    })
    ChannelState.fetchChannels(true)
    router.push({
      name: 'home',
      params: { groupSlug: route.params.groupSlug },
    })
    emit('update:confirmed')
  } catch (_error) {
    notification.create({
      type: 'error',
      title: 'An error occurred...',
      content: `An error occurred while attempting to leave ${ChannelState.state.currentChannelData.name}, please try after sometime.`,
      duration: 3000,
    })
  }
}

async function handleMemberSearch(searchText: string) {
  const trimmedSearchText = searchText.trim()
  // perform search with trimmed search text
  pageNo.value = 1
  if (trimmedSearchText.length > 0) {
    await fetchChannelMembers(
      managersListView.value || selectedRole.value === 'MANAGER'
        ? [ChannelRoles.MANAGER]
        : undefined,
      { limit: 10, pageNo: pageNo.value },
      trimmedSearchText
    )
  } else if (trimmedSearchText.length == 0) {
    await fetchChannelMembers(
      managersListView.value || selectedRole.value === 'MANAGER'
        ? [ChannelRoles.MANAGER]
        : undefined,
      { limit: 10, pageNo: pageNo.value }
    )
  }
}

async function fetchChannelDetails() {
  await ChannelState.setCurrentChannel(route.params.channelSlug as string)
  if (props.type === 'update') {
    modalTitle = ChannelState?.state?.currentChannelData?.name
    modalHelperText = ''
  }

  if (ChannelState?.state?.currentChannelData?.visibility === 'PRIVATE')
    await fetchChannelMembers()
}

async function fetchChannelMembers(
  roles?: Array<ChannelRoles>,
  paginationObject?,
  searchText?: string
) {
  if (!fetchingChannelMembers.value) {
    fetchingChannelMembers.value = true
    if (channelData.value.visibility === 'PRIVATE') {
      await ChannelState.fetchChannelMembers(
        paginationObject
          ? paginationObject
          : { limit: 10, pageNo: pageNo.value },
        roles,
        searchText
      )
    } else {
      // fetch group members in case of public channels
      const members = await GroupState.getGroupMembersDetails(
        undefined,
        GroupMemberStatus.ACTIVE,
        searchText,
        paginationObject
          ? paginationObject
          : { limit: 10, pageNo: pageNo.value },
        false,
        false,
        // passing true here so that group state members are not updated
        true
      )
      if (
        pageNo.value > 1 &&
        ChannelState.state.currentChannelData.channelMembers
      )
        ChannelState.state.currentChannelData.channelMembers = [
          ...ChannelState.state.currentChannelData.channelMembers,
          ...members,
        ]
      else ChannelState.state.currentChannelData.channelMembers = members
    }
    fetchingChannelMembers.value = false
  }
}

function handleToggle(item: string) {
  if (currentActiveTab.value == item) return
  currentActiveTab.value = item

  if (item === 'members') {
    ChannelState.state.currentChannelData.channelMembers = []
    nextPageAvailable.value = true
    pageNo.value = 1
    if (selectedRole.value === 'MANAGER')
      fetchChannelMembers([ChannelRoles.MANAGER])
    else fetchChannelMembers()
  } else {
    managersListView.value = false
  }
}

async function handleAddMembers(members: any) {
  if (members && Array.isArray(members) && members.length) {
    try {
      // filter out any existing members if added erroneously
      members = members.filter(
        m =>
          channelMembers.value.findIndex(cm => cm.contactId === m.contactId) ===
          -1
      )

      let response
      if (managersListView.value) {
        response = await ChannelState.addChannelManagers(members)
      } else {
        response = await ChannelState.addChannelMembers(members)
      }
      notification.create({
        type: 'success',
        title: t(
          managersListView.value
            ? 'channel.notifications.managersAddedSuccess.title'
            : 'channel.notifications.membersAddedSuccess.title'
        ),
        content: t(
          managersListView.value
            ? 'channel.notifications.managersAddedSuccess.content'
            : 'channel.notifications.membersAddedSuccess.content',
          {
            count: response.length,
            channelName: ChannelState.state.currentChannelData.name,
          }
        ),
        duration: 3000,
      })
      ChannelState.state.currentChannelData.membersCount += response.length
    } catch (error: any) {
      notification.create({
        type: 'error',
        title: managersListView.value
          ? t('channel.notifications.error.addFailed.title')
          : t('channel.notifications.error.addMembersFailed.title'),
        content: error.response?.data?.message ?? error.message,
        duration: 3000,
      })
    } finally {
      showAddChannelMembers.value = false
      if (managersListView.value) fetchChannelMembers([ChannelRoles.MANAGER])
      else fetchChannelMembers()
    }
  }
}

async function handleFetchNextPage() {
  pageNo.value += 1
  isFetchingNextPage.value = true
  const prevGroupMembersSize =
    ChannelState.state.currentChannelData.channelMembers.length
  if (selectedRole.value === 'MANAGER')
    await fetchChannelMembers([ChannelRoles.MANAGER], {
      limit: 10,
      pageNo: pageNo.value,
    })
  else await fetchChannelMembers(undefined, { limit: 10, pageNo: pageNo.value })

  // until no data is fetched from the page keep fetching new page,
  // this is because in other pages there are changes because of a deleted contact in a page size of 10 less then 10 contacts could be retuned
  if (
    ChannelState.state.currentChannelData.channelMembers.length <=
    prevGroupMembersSize
  ) {
    nextPageAvailable.value = false
    pageNo.value -= 1
  } else nextPageAvailable.value = true
  isFetchingNextPage.value = false
}

function handleRoleSelectChange(selection: string) {
  selectedRole.value = selection
  nextPageAvailable.value = true
  pageNo.value = 1
  fetchChannelMembers(
    selection === ChannelRoles.MANAGER ? [ChannelRoles.MANAGER] : undefined
  )
}
</script>
<template>
  <CreateChannelWeb
    v-if="!app.state.isMobile && props.type == 'create'"
    :type="type"
    :show="showModal"
    :modal-helper-text="modalHelperText"
    :modal-title="modalTitle"
    :rules="rules"
    :show-icons="showIcons"
    :icon-place-holder-text="iconPlaceHolderText"
    :deleting-channel="deletingChannel"
    :is-loading-modal="isLoadingModal"
    :button-text="buttonText"
    @closeChannelSetting="closeChannelSetting"
    @handle-delete-channel="handleDeleteChannel"
    @update:showIcons="showIcons = $event"
    @update-channel="updateOrCreateChannel"
  />
  <ChannelSettingsWeb
    v-if="!app.state.isMobile && props.type == 'update'"
    :type="type"
    :show="showModal"
    :modal-helper-text="modalHelperText"
    :modal-title="modalTitle"
    :rules="rules"
    :show-icons="showIcons"
    :icon-place-holder-text="iconPlaceHolderText"
    :deleting-channel="deletingChannel"
    :is-loading-modal="isLoadingModal"
    :button-text="buttonText"
    :settings-items="settingsItems"
    :fetching-channel-members="fetchingChannelMembers"
    :current-active-tab="currentActiveTab"
    :managers-list-view="managersListView"
    :selected-role="selectedRole"
    :is-next-members-page-available="nextPageAvailable"
    :is-fetching-members-next-page="isFetchingNextPage"
    @update:searchText="handleMemberSearch"
    @closeChannelSetting="closeChannelSetting"
    @handle-delete-channel="handleDeleteChannel"
    @update:showIcons="showIcons = $event"
    @update-channel="updateOrCreateChannel"
    @handleChangePublicToPrivate="showVisibilityChangeConfirmation = true"
    @handleChangePrivateToPublic="showPrivateToPublicConfirmation = true"
    @handleEnableAnnouncement="showEnableAnnouncementConfirmation = true"
    @handleDisableAnnouncement="showDisableAnnouncementConfirmation = true"
    @leaveChannel="showLeaveConfirmation = true"
    @update:managerListView="managersListView = $event"
    @handleRoleSelection="handleRoleSelectChange"
    @handleTabToggle="handleToggle"
    @showAddChannelMembers="handleShowAddChannelMembers"
    @update:activeTab="currentActiveTab = $event"
    @fetchNextPage="handleFetchNextPage"
  />
  <CreateChannelMobile
    v-if="app.state.isMobile && props.type == 'create'"
    :type="type"
    :show="showModal"
    :modal-helper-text="modalHelperText"
    :modal-title="modalTitle"
    :rules="rules"
    :show-icons="showIcons"
    :icon-place-holder-text="iconPlaceHolderText"
    :deleting-channel="deletingChannel"
    :is-loading-modal="isLoadingModal"
    :button-text="buttonText"
    @closeChannelSetting="closeChannelSetting"
    @handle-delete-channel="handleDeleteChannel"
    @update:showIcons="showIcons = $event"
    @update-channel="updateOrCreateChannel"
    @update:show="showModal = $event"
  />
  <ChannelSettingsMobile
    v-if="app.state.isMobile && props.type == 'update'"
    :type="type"
    :show="showModal"
    :modal-helper-text="modalHelperText"
    :modal-title="modalTitle"
    :rules="rules"
    :show-icons="showIcons"
    :icon-place-holder-text="iconPlaceHolderText"
    :deleting-channel="deletingChannel"
    :is-loading-modal="isLoadingModal"
    :button-text="buttonText"
    :settings-items="settingsItems"
    :fetching-channel-members="fetchingChannelMembers"
    :current-active-tab="currentActiveTab"
    :managers-list-view="managersListView"
    :selected-role="selectedRole"
    :is-next-members-page-available="nextPageAvailable"
    :is-fetching-members-next-page="isFetchingNextPage"
    @closeChannelSetting="closeChannelSetting"
    @handle-delete-channel="handleDeleteChannel"
    @update:showIcons="showIcons = $event"
    @update:searchText="handleMemberSearch"
    @update-channel="updateOrCreateChannel"
    @update:show="showModal = $event"
    @handleChangePublicToPrivate="showVisibilityChangeConfirmation = true"
    @handleChangePrivateToPublic="showPrivateToPublicConfirmation = true"
    @handleEnableAnnouncement="showEnableAnnouncementConfirmation = true"
    @handleDisableAnnouncement="showDisableAnnouncementConfirmation = true"
    @leaveChannel="showLeaveConfirmation = true"
    @update:managerListView="managersListView = $event"
    @handleRoleSelection="handleRoleSelectChange"
    @handleTabToggle="handleToggle"
    @showAddChannelMembers="handleShowAddChannelMembers"
    @update:activeTab="currentActiveTab = $event"
    @fetchNextPage="handleFetchNextPage"
  />
  <AddChannelMembers
    v-if="showAddChannelMembers"
    :show="showAddChannelMembers"
    :manager-view="managersListView"
    @closeAddChannelMembers="
      () => {
        showAddChannelMembers = false
      }
    "
    @addChannelMembers="handleAddMembers($event)"
  />
  <UIConfirmation
    type="error"
    button-type="error"
    :show="showDeleteConfirmation"
    :title="t('channel.deleteChannel')"
    :message="
      channelData.visibility === 'PRIVATE'
        ? t('channel.deleteChannelMessageWithCourse')
        : t('channel.deleteChannelMessage')
    "
    @update:show="showDeleteConfirmation = $event"
    @update:confirmed="deleteChannel"
  />
  <UIConfirmation
    type="primary"
    button-type="primary"
    :height="380"
    :show="showVisibilityChangeConfirmation"
    :title="t('post.privateChannel')"
    :is-message-html="true"
    :message="`
    <p>${t('channel.changeToPrivateChannelConfirmation.mainMessage')}
      <strong>
        ${t('channel.changeToPrivateChannelConfirmation.mainMessage2')}
      </strong>
    </p>
    <ul>
      <li>${t('channel.changeToPrivateChannelConfirmation.pointer2')}</li>
    </ul>`"
    @update:show="showVisibilityChangeConfirmation = $event"
    @update:confirmed="handleChangePublicToPrivate"
  />
  <UIConfirmation
    type="primary"
    button-type="primary"
    :height="380"
    :show="showPrivateToPublicConfirmation"
    :title="t('channel.publicChannel')"
    :is-message-html="true"
    :message="`
    <p>${t('channel.changeToPublicChannelConfirmation.mainMessage')}
      <strong>
        ${t('channel.changeToPublicChannelConfirmation.mainMessage2')}
      </strong>
    </p>
    <ul>
      <li>${t('channel.changeToPublicChannelConfirmation.pointer1')}</li>
      <li>${t('channel.changeToPublicChannelConfirmation.pointer2')}</li>
    </ul>`"
    @update:show="showPrivateToPublicConfirmation = $event"
    @update:confirmed="handleChangePrivateToPublic"
  />
  <UIConfirmation
    type="primary"
    button-type="primary"
    :height="380"
    :show="showEnableAnnouncementConfirmation"
    :title="t('channel.readOnlyChannel')"
    :is-message-html="true"
    :message="`
      <p>${t('channel.enableMemberPostingConfirmation.mainMessage')}
        <strong>
          ${t('channel.enableMemberPostingConfirmation.mainMessage2')}
        </strong>
      </p>
      <ul>
        <li>${t('channel.enableMemberPostingConfirmation.pointer1')}</li>
        <li>${t('channel.enableMemberPostingConfirmation.pointer2')}</li>
    </ul>`"
    @update:show="showEnableAnnouncementConfirmation = $event"
    @update:confirmed="setAnnouncementPostingState(false)"
  />
  <UIConfirmation
    type="primary"
    button-type="primary"
    :height="380"
    :show="showDisableAnnouncementConfirmation"
    :title="t('channel.readOnlyChannel')"
    :is-message-html="true"
    :message="`
    <p>${t('channel.disableMemberPostingConfirmation.mainMessage')}
      <strong>
        ${t('channel.disableMemberPostingConfirmation.mainMessage2')}
      </strong>
    </p>
    <ul>
      <li>${t('channel.disableMemberPostingConfirmation.pointer1')}</li>
      <li>${t('channel.disableMemberPostingConfirmation.pointer2')}</li>
    </ul>`"
    @update:show="showDisableAnnouncementConfirmation = $event"
    @update:confirmed="setAnnouncementPostingState(true)"
  />
  <UIConfirmation
    type="error"
    button-type="error"
    :show="showLeaveConfirmation"
    :title="t('channel.leaveChannel')"
    :message="t('channel.leaveChannelConfirmation')"
    @update:show="showLeaveConfirmation = $event"
    @update:confirmed="handleLeaveChannel"
  />
</template>
<style scoped>
.icons-container {
  box-sizing: revert !important;
}
</style>
